import React from "react";
import LoginForm from "./LoginForm";
import { Target, TrendingUp, BarChart3, Zap, Sun, Moon } from "lucide-react";
import Logo from "../../../assets/image.png";
import { useTheme } from "../../../contexts/ThemeContext";

const Login: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen flex bg-base relative">
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 z-20 p-2 rounded-md text-secondary-t hover:bg-hover-bg transition-colors"
        title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      >
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>

      {/* Left Side - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, var(--blue), var(--violet))" }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center mb-8">
            <img
              src={Logo}
              alt="Adrsta Logo"
              className="h-16 w-auto bg-white rounded-lg shadow-lg p-2"
            />
          </div>

          <div className="space-y-6 text-white mt-12">
            <h2 className="text-4xl font-bold leading-tight">
              Welcome to Your<br />Marketing Science Platform
            </h2>
            {/* <p className="text-xl text-blue-100">
              360° Performance Optimization Platform
            </p> */}
          </div>
        </div>

        {/* Features */}
        <div className="relative z-10 grid grid-cols-1 gap-4 text-white">
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Synthetic Control</h3>
              <p className="text-sm text-blue-100">Measure causal impact with advanced statistical methods</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Marketing Mix Modeling (MMM)</h3>
              <p className="text-sm text-blue-100">Optimize marketing spend across all channels</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Bid Optimization</h3>
              <p className="text-sm text-blue-100">Maximize ROI with intelligent bidding strategies</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Performance Agent</h3>
              <p className="text-sm text-blue-100">One click Measurement and Optimization</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-white/60 text-sm">
          © {new Date().getFullYear()} Adrsta. All rights reserved.
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img
              src={Logo}
              alt="Adrsta Logo"
              className="h-16 w-auto"
            />
          </div>

          {/* Login Card */}
          <div className="bg-elevated rounded-2xl shadow-xl border border-[var(--border-default)] p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-primary-t mb-2">Welcome back</h2>
              <p className="text-secondary-t">Please sign in to your account</p>
            </div>

            <LoginForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
